/**
 * Reading an Apollo operation result without mistaking a FAILURE for an empty one.
 *
 * Every Hub-family Apollo client runs with `errorPolicy: "all"` (it is a
 * requirement of the `DeclareDefaultOptions` augmentation — see `./types`), so a
 * failed `client.query(...)` / `client.mutate(...)` does NOT throw: it resolves
 * with `data: undefined` and an `error`. Code that goes straight to
 * `result.data?.xCollection?.edges?.[0]?.node` therefore answers a failed read as
 * "no rows" and a failed write as `undefined`, with nothing logged. `tsc` is
 * satisfied (`?.` was written to tolerate `undefined`), the happy path behaves
 * identically, and nothing can go red.
 *
 * `requireData` is the ONE place that decides "this result is a failure": call it
 * on every result before reading `.data`.
 *
 *   const data = requireData(await client.query({ query: GetThingsDocument }), "things");
 *   const things = data?.thingsCollection?.edges ?? [];
 *
 * Pure on purpose — no logger, no sanitizer, no app code — so Hub, Nonprofit Hub
 * and the Meridian package can all use it. A caller that needs model- or
 * user-safe text builds it from the thrown error (Hub's `queryData` does).
 */

/** The slice of an Apollo `query`/`mutate` result this needs; structural so any client fits. */
export interface GraphQLResultLike<TData> {
  data?: TData | null;
  error?: unknown;
}

/** An operation failed. `cause` is the raw error; `message` is a one-line description of it. */
export class GraphQLResultError extends Error {
  readonly cause: unknown;

  constructor(message: string, options: { cause: unknown }) {
    super(message);
    this.name = "GraphQLResultError";
    this.cause = options.cause;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * A one-line description of a failure value. A PostgREST/pg_graphql failure can be
 * a plain object whose `message` is EMPTY with the reason in `details` or `hint`,
 * so reading only `.message` loses exactly the case that matters.
 */
function describeCause(error: unknown): string {
  if (typeof error === "string" && error.length > 0) return error;
  if (isRecord(error)) {
    for (const key of ["message", "details", "hint"] as const) {
      const value = error[key];
      if (typeof value === "string" && value.length > 0) return value;
    }
    if (typeof error.code === "string" && error.code.length > 0) return `error code ${error.code}`;
  }
  return "the operation failed without reporting a reason";
}

/** Did this result fail? An empty `data` with no `error` is a successful read of nothing. */
export function hasGraphQLError(result: GraphQLResultLike<unknown>): boolean {
  return result.error !== undefined && result.error !== null && result.error !== false;
}

/**
 * The result's data, or a thrown `GraphQLResultError` if the operation failed.
 *
 * Partial data alongside an error is still a failure (`errorPolicy: "all"` hands
 * back both): a half-read list that looks complete is worse than an error.
 * `data: undefined`/`null` with no error is a read that returned nothing and is
 * returned as `undefined` — that is the caller's own "not found".
 *
 * @param operation what was being attempted, in prose, for the message ("deals",
 *   "app access grant"); optional.
 */
export function requireData<TData>(
  result: GraphQLResultLike<TData>,
  operation?: string,
): TData | undefined {
  if (!hasGraphQLError(result)) return result.data ?? undefined;
  const what = operation ? `${operation}: ` : "";
  throw new GraphQLResultError(`${what}${describeCause(result.error)}`, { cause: result.error });
}
